import { formatDifferenceInHoursAndMinutes } from '@backoffice-frontend/common'
import {
  AdConnectivityState,
  NotInServiceReason,
} from '@backoffice-frontend/graphql'
import { useToggle } from '@backoffice-frontend/hooks'
import {
  Collapse,
  Divider,
  MoiaGrid,
  useTheme,
} from '@backoffice-frontend/patterns'
import {
  CheckCircleSolidIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  QuestionCircleIcon,
  XCircleSolidIcon,
} from '@moia-dev/pace-icons'
import type { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import { usePoolingReasonTranslations } from '../../locales/mappings'
import type { VehicleDrawerStatusBarVehicleFragment } from './VehicleDrawerStatusBar.hook'
import { VehicleDrawerStatusBarIndicator } from './VehicleDrawerStatusBarIndicator'
import { VehicleDrawerStatusBarRow } from './VehicleDrawerStatusBarRow'

export const VehicleDrawerStatusBar = ({
  vehicle,
  className,
}: {
  vehicle: VehicleDrawerStatusBarVehicleFragment
  className?: string
  showExpandIcon?: boolean
}) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const [contentVisible, toggleContentVisible] = useToggle(false)
  const poolingReasonTranslations = usePoolingReasonTranslations()

  const formatDuration = (dateString?: string | null) =>
    dateString
      ? t('fleetMap_sinceTimeDifference_label', {
          duration: formatDifferenceInHoursAndMinutes(
            new Date(dateString),
            new Date(),
          ),
        })
      : ''

  const notInServiceReasons =
    vehicle.notInServiceReasons
      ?.filter(reason => knownNotInServiceReasons.includes(reason))
      .map(reason => getNotInServiceReason(reason, t)) || []

  const isOnline = (state?: AdConnectivityState | null) =>
    state === AdConnectivityState.Connected

  return (
    <div
      className={className}
      css={{
        padding: theme.spacing(2, 2, 1, 2),
        borderBottom: '1px solid var(--color-accessory-reduced)',
      }}
    >
      <MoiaGrid
        css={{
          display: 'flex',
        }}
        gridGap={4}
      >
        <VehicleDrawerStatusBarIndicator
          label={
            vehicle.inService !== null
              ? vehicle.inService
                ? t('common_bookable_label')
                : t('common_notBookable_label')
              : t('common_bookability_label')
          }
          variant={
            vehicle.inService !== null
              ? vehicle.inService
                ? 'success'
                : 'critical'
              : 'neutral'
          }
          icon={
            vehicle.inService !== null ? (
              vehicle.inService ? (
                <CheckCircleSolidIcon />
              ) : (
                <XCircleSolidIcon />
              )
            ) : (
              <QuestionCircleIcon />
            )
          }
          tooltip={
            notInServiceReasons.length > 1 ? (
              <div
                css={theme => ({
                  flexDirection: 'column',
                  gap: theme.spacing(0),
                })}
              >
                {notInServiceReasons.map(reason => {
                  return <div key={reason}>{reason}</div>
                })}
              </div>
            ) : undefined
          }
          subtitle={
            notInServiceReasons.length > 0
              ? notInServiceReasons.length > 1
                ? `${notInServiceReasons[0]} ${notInServiceReasons.length - 1}`
                : notInServiceReasons[0]
              : undefined
          }
        />

        <VehicleDrawerStatusBarIndicator
          label={
            vehicle.pooling !== null
              ? vehicle.pooling.enabled
                ? t('common_poolingOn_label')
                : t('common_poolingOff_label')
              : t('common_pooling_label')
          }
          variant={
            vehicle.pooling !== null
              ? vehicle.pooling.enabled
                ? 'success'
                : 'critical'
              : 'neutral'
          }
          icon={
            vehicle.pooling !== null ? (
              vehicle.pooling.enabled ? (
                <CheckCircleSolidIcon />
              ) : (
                <XCircleSolidIcon />
              )
            ) : (
              <QuestionCircleIcon />
            )
          }
          subtitle={
            vehicle.pooling?.enabled || !vehicle.pooling?.reason ? (
              formatDuration(vehicle.pooling?.enabledUpdatedAt)
            ) : (
              <>
                {poolingReasonTranslations(vehicle.pooling.reason)}
                <br />
                {formatDuration(vehicle.pooling.enabledUpdatedAt)}
              </>
            )
          }
        />
        {!vehicle.isSelfDriving && (
          <VehicleDrawerStatusBarIndicator
            variant={vehicle.pooling?.connected ? 'success' : 'critical'}
            icon={
              vehicle.pooling !== null ? (
                vehicle.pooling.connected ? (
                  <CheckCircleSolidIcon />
                ) : (
                  <XCircleSolidIcon />
                )
              ) : (
                <QuestionCircleIcon />
              )
            }
            label={
              vehicle.pooling?.connected
                ? t('common_connected_label')
                : t('common_disconnected_label')
            }
            subtitle={formatDuration(vehicle.pooling?.connectedUpdatedAt)}
          />
        )}
        {vehicle.isSelfDriving && (
          <VehicleDrawerStatusBarIndicator
            label={
              vehicle.pooling !== null
                ? vehicle.pooling.connected
                  ? t('adFleetMap_SdsConnected_label')
                  : t('adFleetMap_SdsDisconnected_label')
                : t('adFleetMap_Sds_label')
            }
            variant={
              vehicle.pooling !== null
                ? vehicle.pooling.connected
                  ? 'success'
                  : 'critical'
                : 'neutral'
            }
            icon={
              vehicle.pooling !== null ? (
                vehicle.pooling.connected ? (
                  <CheckCircleSolidIcon />
                ) : (
                  <XCircleSolidIcon />
                )
              ) : (
                <QuestionCircleIcon />
              )
            }
            subtitle={formatDuration(vehicle.pooling?.connectedUpdatedAt)}
          />
        )}
        {vehicle.isSelfDriving && (
          <div
            css={{
              cursor: 'pointer',
              marginRight: 0,
              marginLeft: 'auto',
            }}
            onClick={toggleContentVisible}
            role="button"
            tabIndex={-1}
          >
            {contentVisible ? <ChevronUpIcon /> : <ChevronDownIcon />}
          </div>
        )}
      </MoiaGrid>

      {vehicle.isSelfDriving && (
        <Collapse
          in={contentVisible}
          css={{
            '& .separator': {
              gridColumn: '1 / -1',
            },
          }}
        >
          <Divider
            title={t('adFleetMap_statusOfSystemComponents_header')}
            css={{ margin: theme.spacing(3, 0, 3, 0) }}
          />
          <div
            css={{
              display: 'grid',
              alignItems: 'center',
              columnGap: theme.spacing(4),
              rowGap: theme.spacing(1),
              gridTemplateColumns: '1fr auto auto',
            }}
          >
            <VehicleDrawerStatusBarRow
              title={t('adFleetMap_vehicleStatusVehicleMissionControl_label')}
              isOnline={isOnline(
                vehicle.latestADTelemetry?.missionConnectivityState
                  ?.connectivityState,
              )}
              subtitle={formatDuration(
                vehicle.latestADTelemetry?.missionConnectivityState?.timestamp,
              )}
            />
            <Divider borderStyle="dashed" className="separator" />
            <VehicleDrawerStatusBarRow
              title={t('adFleetMap_vehicleStatusOperatorInteractions_label')}
              isOnline={isOnline(
                vehicle.latestADTelemetry?.signalConnectivityState
                  ?.connectivityState,
              )}
              subtitle={formatDuration(
                vehicle.latestADTelemetry?.signalConnectivityState?.timestamp,
              )}
            />
            <Divider borderStyle="dashed" className="separator" />
            <VehicleDrawerStatusBarRow
              title={t('adFleetMap_vehicleStatusCabinSafety_label')}
              isOnline={isOnline(
                vehicle.latestADTelemetry?.cabinConnectivityState
                  ?.connectivityState,
              )}
              subtitle={formatDuration(
                vehicle.latestADTelemetry?.cabinConnectivityState?.timestamp,
              )}
            />
          </div>
        </Collapse>
      )}
    </div>
  )
}

const knownNotInServiceReasons = [
  NotInServiceReason.Inactive,
  NotInServiceReason.NoRecentValidLocation,
  NotInServiceReason.OutdatedHubReturn,
  NotInServiceReason.Unresponsive,
]

const getNotInServiceReason = (
  reason: NotInServiceReason,
  t: TFunction<'fleet-maps-common', undefined>,
) => {
  switch (reason) {
    case NotInServiceReason.Inactive:
      return t('fleetMap_options_notInServiceReasonInactive_label')
    case NotInServiceReason.NoRecentValidLocation:
      return t('fleetMap_options_notInServiceReasonNoRecentValidLocation_label')
    case NotInServiceReason.OutdatedHubReturn:
      return t('fleetMap_options_notInServiceReasonOutdatedHubReturn_label')
    case NotInServiceReason.Unresponsive:
      return t('fleetMap_options_notInServiceReasonUnresponsive_label')
  }
  return reason
}
